<template>
  <TopLine :style="styleTopline" :title="topLineTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
  <div id="explain-tour" :style="styleMiddle" class="container" >
    <div v-html="currentText"></div>
  </div>
  <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
</template>

<script setup>

  import { computed } from "@vue/reactivity"
  import { ref , watch } from 'vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import { useGetData, generateErrorAlertText } from '@/composables/getData.js'
  import ToolBar from '@/components/ToolBar.vue'
  import TopLine from '@/components/TopLine.vue'
  import { translateEnglishWords, translatePhrase } from "@/composables/translation";

  const emit = defineEmits(['toolbar-click'])

  const props = defineProps({
    screen : {
      type : String,
      default : ""
    },
    wordsSpoken : String,
    wordsSpokenTrigger : Number
  })

  const TOOLBAR_NEXT= 0
  const TOOLBAR_BACK = 1
  const TOOLBAR_NEW = 2
  const TOOLBAR_TOGGLE_LANGUAGE = 3

  const toolbarElements = [
    { index : TOOLBAR_NEXT, label : translateEnglishWords("Next") ,disabled : true},
    { index : TOOLBAR_BACK, label : translateEnglishWords("Back") ,disabled : true},
    { index : TOOLBAR_NEW, label : translateEnglishWords("New") ,disabled : true}
    ]

  const toolbarTrigger = ref(0)

  const connectionStore = useConnectionStore()
  const languageInUse = connectionStore.getLanguageInUse

  if (languageInUse != "en")
      toolbarElements.push({ index : TOOLBAR_TOGGLE_LANGUAGE, label : translateEnglishWords("English") ,disabled : false})

  var showingTranslated = true

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)

  const windowSizeStore = useWindowSizeStore()

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))
  const topLineTitle = ref(translateEnglishWords("Explanation"))

  var screenName = props.screen
  var showingNew = false

  const loadingPageText = "<h3>" + translatePhrase("Loading Tour Explanation Pages") + "</h3>"
  var currentIndex = 0
  const currentText = ref(loadingPageText)
  var currentCount = 0
  var currents = []

  var pages = []
  var pageCount = 0

  var news = []
  var newCount = 0

  if (screenName.startsWith("ERROR:"))
        createFakeErrorPage(screenName)
  else
  if (screenName.startsWith("HTML:"))
        createFakeHtmlPage(screenName)
  else
  if (screenName == "newmain") {
    showingNew = true
    screenName = "main"
  }

  const screen = computed({ get() { return props.screen } })
  const wordsSpoken = computed({ get() { return props.wordsSpoken } })
  const wordsSpokenTrigger = computed({ get() { return props.wordsSpokenTrigger } })

  getTourPages(languageInUse)

  watch(wordsSpokenTrigger, (newVal,oldVal) => {
    //console.log('*********** words spoken changed',props.wordsSpoken)
    parseExplainTourWords(props.wordsSpoken)
  })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  function createFakeErrorPage(errorString) {
      var json = [];
      json['programRun'] = "get1_verbal_tour.php"
      json['page_count'] = 1
      json['new_count'] = 0
      json['page0'] = "<h3>*** ERROR ALERT ***</h3><p>" + errorString.substring(6).replace("\n","<p>")
      processTourJsonResponse(json)
  }

  function createFakeHtmlPage(htmlString) {
      var json = [];
      json['programRun'] = "get1_verbal_tour.php"
      json['page_count'] = 1
      json['new_count'] = 0
      json['page0'] = htmlString.substring(5)
      processTourJsonResponse(json)
  }

  function updateToolbarDisabledStates(buttonStates) {
    //console.log("update button states",buttonStates)
    toolbarElements[TOOLBAR_NEXT].disabled = (buttonStates.next == "disabled")
    toolbarElements[TOOLBAR_BACK].disabled = (buttonStates.back == "disabled")
    toolbarElements[TOOLBAR_NEW].disabled = (buttonStates.new == "disabled")
    toolbarElements[TOOLBAR_NEW].label = buttonStates.newLabel

    toolbarTrigger.value++;
  }

  function toplineClick(whatClicked) {
    //console.log("topline in multi-options",whatClicked)
    switch (whatClicked) {
      case "done" :
        //processWords("command done")
        emit("toolbar-click","done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        emit("toolbar-click",microphoneIsMuted.value ? "mute" : "listen")
        break
    }
  }

  function toolbarClick(arrayIndex) {
    //console.log("in tour explain toolbar click",arrayIndex)
    switch (arrayIndex) {
      case TOOLBAR_NEXT :
        processNext()
        break
      case TOOLBAR_BACK :
        processBack()
        break
      case TOOLBAR_NEW :
        processNew()
        break
      case TOOLBAR_TOGGLE_LANGUAGE :
        processToggleLanguage()
        break
    }
  }

  function processToggleLanguage() {
    showingTranslated = !showingTranslated
    toolbarElements[TOOLBAR_TOGGLE_LANGUAGE].label = (showingTranslated) ? 
                                    translateEnglishWords("English") : 
                                    translateEnglishWords("Translated")
    toolbarTrigger.value++
    getTourPages( (showingTranslated) ? languageInUse : "en")
  }

  function processNew() {
    if (showingNew) {
      if (pageCount == 0)
        return
      currentCount = pageCount
      currents = pages
      showingNew = false
    }
    else {
      if (newCount == 0)
        return
      currentCount = newCount
      currents = news
      showingNew = true
    }

    currentIndex = 0
    currentText.value = currents[0]

    const buttonStates = {
              next : (currentCount <= 1) ? "disabled" : "enabled",
              back : "disabled",
              new : (newCount == 0) ? "disabled" : "enabled",
              newLabel : (showingNew) ? translateEnglishWords("General") : 
                                        translateEnglishWords("New")
    }
    updateToolbarDisabledStates(buttonStates)
    scrollElement("explain-tour","top")
  }

  function processNext() {
    var buttonStates = {}

    buttonStates.back = (currentCount > 0) ? "enabled" : "disabled"

    if (currentIndex < currentCount - 1) {
      currentIndex++;
      showPage(currentIndex)
    }

    buttonStates.next = (currentIndex < currentCount - 1) ? "enabled" : "disabled"
    buttonStates.new = (newCount == 0) ? "disabled" : "enabled"
    buttonStates.newLabel = (showingNew) ? translateEnglishWords("General") : 
                            translateEnglishWords("New")
    updateToolbarDisabledStates(buttonStates)
    scrollElement("explain-tour","top")
  }

  function processBack() {
    var buttonStates = {}

    buttonStates.next = (currentCount > 0) ? "enabled" : "disabled"

    if (currentIndex > 0) {
      currentIndex--;
      buttonStates.back = (currentIndex == 0) ? "disabled" : "enabled"
      showPage(currentIndex)
    }

    buttonStates.new = (newCount == 0) ? "disabled" : "enabled"
    buttonStates.newLabel = (showingNew) ? translateEnglishWords("General") : 
                  translateEnglishWords("New")
    updateToolbarDisabledStates(buttonStates)
    scrollElement("explain-tour","top")

  }

  function parseExplainTourWords(str) {
    //console.log("string in tour ",str)
  
    const words = str.split(" ")

    //console.log("words",words,words.length)

    if (words.length == 0)
        return (-1);

    var currentWord = (words.length > 0) ? words[0] : "xyz"
    var nextWord = (words.length > 1) ? words[1] : "xyz"
    var nextNextWord = (words.length > 2) ? words[2] : "xyz"
          
    //console.log("current word",currentWord)

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore")
            return (-1)

        if (currentWord == "command") {
          switch (nextWord) {
            case "mute" :
              if (microphoneIsMuted.value == true)
                break
              microphoneIsMuted.value = true 
              connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
              emit("toolbar-click","mute")
              break
            case "listen" :
              if (microphoneIsMuted.value == false)
                break
              microphoneIsMuted.value = false 
              connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
              emit("toolbar-click","listen")
              break
            case "scroll" :
              scrollElement("explain-tour",nextNextWord)
              break
            case "next" :
              if (!toolbarElements[TOOLBAR_NEXT].disabled)
                processNext()
              break
            case "back" :
              if (!toolbarElements[TOOLBAR_BACK].disabled)
                processBack()
              break
            case "general" :
            case "new" :
              if (!toolbarElements[TOOLBAR_NEW].disabled)
                processNew()
              break
            case "done" :
              emit("toolbar-click","done")
              break
          }
          return (-1)
        }
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (-1)
  }

  function showPage(currentNumber) {
    if (currentNumber >= 0 && currentNumber < currentCount)
      currentText.value = currents[currentNumber]
  }

  // replaces the XYZb_l_a_hXYZ with word translations.
  
  function patchUpWords(page,languageUsed) {
    var pieces = page.split("~")

    //console.log("PIECE COUNT",pieces.length,languageUsed)
    if (pieces.length == 1)
      return (page)

    if (pieces.length % 2 == 0) {
      console.log("************************ Explain Page Has unbalanced XYZ ***********")
    }

    if (languageUsed != "en") {
      for (var i=1; i < pieces.length; i += 2) {
        const collapsed = pieces[i];
        //console.log("collapsed",collapsed)
        pieces[i] = translateEnglishWords(collapsed)
      }
    }

    //console.log("PIECES",pieces)
    return (pieces.join(""))
  }

  function processTourJsonResponse (json) {
    //console.log(" tour callback",json)

    // fake the page using the error
    if (json.hasOwnProperty('error')) {
      json['programRun'] = "getxyz_verbal_tour.php"
      json['page_count'] = 1
      json['new_count'] = 0
      json['page0'] = "<h3>*** ERROR ALERT ***</h3><p>" + generateErrorAlertText(json)
    }

    const languageUsed = json['language']
    pageCount = json['page_count']
    newCount = json['new_count']

    pages = []
    news = []

    if (pageCount == 0 && newCount == 0)
      return

    for (var i=0; i < pageCount; i++) {
      var pageText = json['page' + i]
      pageText = patchUpWords(pageText,languageUsed)
      pages.push(pageText)
    }
    for (var i=0; i < newCount; i++) {
      var newText = json['new' + i]
      newText = patchUpWords(newText,languageUsed)
      news.push(newText)
    }

    //console.log("show new",showingNew)
    //console.log("pages",pages)
    currentIndex = 0
    currentCount = (showingNew) ? newCount : pageCount
    currents = (showingNew) ? news : pages
    currentText.value = currents[0]

    const buttonStates = {
              next : (pageCount <= 1) ? "disabled" : "enabled",
              back : "disabled",
              new : (newCount == 0) ? "disabled" : "enabled",
              newLabel : (showingNew) ? translateEnglishWords("General") : 
                                        translateEnglishWords("New")
    }
    updateToolbarDisabledStates(buttonStates)
  }

  function getTourPages(language) {

    const connectionStore = useConnectionStore()
    const verbalVersion = connectionStore.getVersion

    var formData = {
      os : "web",
      version : verbalVersion,
      screen : screenName,
      language : language
    }
    
    console.log("*** get tour formdata",formData)
    useGetData("get5_verbal_tour.php", formData,processTourJsonResponse)
  }

</script>

<style scoped>

.container {
    font-family: Verdana, Geneva, Tahoma, sans-serif;
}
.container :deep(p) {
  margin-bottom : 10px;
}

.container :deep(h3) {
  margin-bottom : 20px;
}

.container :deep(ul) {
  margin-bottom : 10px;
}

.container :deep(li) {
  margin-left : 20px;
}

.container :deep(b) {
  font-weight: bold;
}
.container :deep(strong) {
  font-weight: bold;
  /* color:yellow; */
}

</style>
